import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { sessionState, useChatInteract } from '@chainlit/react-client';

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog';

import type { AgentDebugView } from '../localCommands';
import { type ContextUsage, ContextView } from './ContextView';

interface ToolInfo {
    name: string;
    description?: string;
}

interface AgentDebugSnapshot {
    available?: boolean;
    context?: ContextUsage | null;
    mode?: string;
    is_report_embedded?: boolean;
    // Per-report capabilities chosen by the report author in BIG (report tools /
    // the attached MCP server), delivered as iframe params → report_embed_context.
    // These are NOT user-toggleable, so seeing them here is the only way to tell
    // a mis-set report setting from a chat bug.
    report_plain_chat?: boolean;
    report_mcp_enabled?: boolean;
    report_mcp_servers?: string[] | null;
    sources_shown?: boolean;
    agent?: string;
    model?: string;
    report_tools?: ToolInfo[];
    general_tools?: ToolInfo[];
    mcp_tools?: ToolInfo[];
    report_system_prompt_present?: boolean;
    system_prompt?: string;
}

interface AgentDebugDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    // Incremented by the composer on every `/agent_debug` so we always re-fetch a
    // fresh snapshot, even if the modal was already open.
    requestToken: number;
    view: AgentDebugView;
}

const Bool = ({ value }: { value?: boolean }) => (
    <code className="bg-muted px-1 rounded text-xs">
        {value ? 'true' : 'false'}
    </code>
);

const ToolList = ({ title, tools }: { title: string; tools?: ToolInfo[] }) => (
    <div className="space-y-1">
        <div className="font-medium text-sm">
            {title}{' '}
            <span className="font-normal text-muted-foreground">
                ({tools?.length ?? 0})
            </span>
        </div>
        {tools && tools.length > 0 ? (
            <ul className="space-y-1.5">
                {tools.map((t) => (
                    <li key={t.name} className="text-sm">
                        <code className="bg-muted px-1 rounded">{t.name}</code>
                        {t.description ? (
                            <span className="block mt-0.5 text-muted-foreground text-xs whitespace-pre-wrap">
                                {t.description}
                            </span>
                        ) : null}
                    </li>
                ))}
            </ul>
        ) : (
            <div className="text-muted-foreground text-sm italic">none</div>
        )}
    </div>
);

const AgentDebugDialog = ({
    open,
    onOpenChange,
    requestToken,
    view
}: AgentDebugDialogProps) => {
    const contextOnly = view === 'context';
    const session = useRecoilValue(sessionState);
    const { windowMessage } = useChatInteract();
    const [data, setData] = useState<AgentDebugSnapshot | null>(null);

    // Listen for the backend's response (a dedicated event, not forwarded to the
    // host window). Stays subscribed while mounted so a re-request always lands.
    useEffect(() => {
        const socket = session?.socket;
        if (!socket) return;
        const handler = (payload: AgentDebugSnapshot) => setData(payload);
        socket.on('agent_debug_response', handler);
        return () => {
            socket.off('agent_debug_response', handler);
        };
    }, [session?.socket]);

    // Request a fresh snapshot on every `/agent_debug` invocation (token bump),
    // not just on the open transition — so re-running it always reflects the
    // current mode even if the modal was already open.
    useEffect(() => {
        if (requestToken > 0) {
            setData(null);
            windowMessage({ type: 'agent_debug' });
        }
    }, [requestToken, windowMessage]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="flex flex-col gap-0 bg-background shadow-xl p-0 border border-border w-[820px] max-w-[95vw] h-[80vh] max-h-[820px] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <DialogHeader className="px-5 pt-5 pb-3 border-border border-b">
                    <DialogTitle>
                        {contextOnly ? '📊 Context' : '🛠️ Agent debug'}
                    </DialogTitle>
                    <DialogDescription>
                        {contextOnly
                            ? 'Context window usage, request history and session cost for this chat.'
                            : 'Everything this agent currently sees — mode, model, the tools available to it (with their descriptions), and the full system prompt.'}
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 space-y-5 px-5 py-4 overflow-y-auto">
                    {data === null ? (
                        <div className="text-muted-foreground text-sm">
                            Loading…
                        </div>
                    ) : contextOnly ? (
                        <ContextView context={data.context} />
                    ) : data.available === false ? (
                        <div className="space-y-2">
                            <div className="text-sm">
                                <span className="font-medium">Mode:</span>{' '}
                                {data.mode}
                            </div>
                            <div className="text-sm">
                                is_report_embedded:{' '}
                                <Bool value={data.is_report_embedded} /> -
                                report_plain_chat:{' '}
                                <Bool value={data.report_plain_chat} /> -
                                report_mcp_enabled:{' '}
                                <Bool value={data.report_mcp_enabled} />
                            </div>
                            <div className="text-muted-foreground text-sm italic">
                                Send a message first — the full tool list and
                                system prompt are captured when the agent is
                                built for your first message.
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="space-y-1.5">
                                <div className="text-sm">
                                    <span className="font-medium">Mode:</span>{' '}
                                    {data.mode}
                                </div>
                                <div className="text-muted-foreground text-sm">
                                    is_report_embedded:{' '}
                                    <Bool value={data.is_report_embedded} /> ·
                                    report_plain_chat:{' '}
                                    <Bool value={data.report_plain_chat} /> ·
                                    report_mcp_enabled:{' '}
                                    <Bool value={data.report_mcp_enabled} />
                                    {data.report_mcp_servers?.length ? (
                                        <>
                                            {' '}
                                            (
                                            <code className="bg-muted px-1 rounded text-xs">
                                                {data.report_mcp_servers.join(
                                                    ', '
                                                )}
                                            </code>
                                            )
                                        </>
                                    ) : null}{' '}
                                    - sources shown:{' '}
                                    <Bool value={data.sources_shown} />
                                </div>
                                <div className="text-sm">
                                    <span className="font-medium">Model:</span>{' '}
                                    {data.model}
                                </div>
                                <div className="text-sm">
                                    <span className="font-medium">
                                        Per-report system prompt:
                                    </span>{' '}
                                    {data.report_system_prompt_present
                                        ? 'present'
                                        : 'none'}
                                </div>
                            </div>

                            <ToolList
                                title="Report tools"
                                tools={data.report_tools}
                            />
                            <ToolList
                                title="General tools"
                                tools={data.general_tools}
                            />
                            <ToolList
                                title="MCP tools"
                                tools={data.mcp_tools}
                            />

                            <div className="space-y-1">
                                <div className="font-medium text-sm">
                                    System prompt sent to the model
                                </div>
                                <pre className="bg-muted p-3 rounded overflow-x-auto text-xs whitespace-pre-wrap">
                                    {data.system_prompt}
                                </pre>
                            </div>

                            <div className="text-muted-foreground text-xs italic">
                                The agent also receives the conversation history
                                (the messages in this thread) in addition to the
                                above.
                            </div>
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AgentDebugDialog;
